import type { NextPage } from 'next'
import type { AppProps } from 'next/app'
import dynamic from 'next/dynamic';
import Head from 'next/head';
import 'swiper/css'
import "swiper/css/navigation";
import "swiper/css/pagination";
import '../styles/global.scss'
import '../styles/bootstrap-overrides.css'
import "sweetalert2/src/sweetalert2.scss";

import AppLayout from "@/layouts/App";
import Frontend from "@/layouts/Frontend";
import Blank from '@/layouts/blank';
import Merchandise from '@/layouts/MerchandiseLayout';

//store
//reducer
import { wrapperStore } from '@/store'

// Dynamically import AuthProvider with SSR disabled
const DynamicAuthProvider = dynamic(
  () => import('@/contexts/AuthContext').then(mod => mod.AuthProvider),
  { ssr: false }
);

// Dynamically import Redux Provider with SSR disabled
const DynamicReduxProvider = dynamic(
  () => import('react-redux').then(mod => {
    const { Provider } = mod;
    return ({ children, store }: { children: React.ReactNode, store: any }) => (
      <Provider store={store}>{children}</Provider>
    );
  }),
  { ssr: false }
);

const layouts: any = {
  "Blank": Blank,
  "Frontend": Frontend,
  "Merchandise": Merchandise
};

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  layout: string
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

function AppWithLayout({ Component, ...rest }: AppPropsWithLayout) {
  const layoutName = layouts[Component.layout] || layouts['Frontend']
  const Layout = layoutName || ((children: any) => <>{children}</>);

  // Use next-redux-wrapper's useWrappedStore hook to manage store
  // This properly handles hydration between server and client
  const { store, props } = wrapperStore.useWrappedStore(rest);
  const { pageProps } = props;

  // Use environment variables for site name and title suffix
  const siteName = process.env.NEXT_PUBLIC_SITE_NAME || 'G-School';
  const titleSuffix = process.env.NEXT_PUBLIC_TITLE_SUFFIX ? ` - ${process.env.NEXT_PUBLIC_TITLE_SUFFIX}` : '';
  const pageTitle = `${siteName}${titleSuffix}`;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <DynamicReduxProvider store={store}>
        <DynamicAuthProvider>
          <AppLayout>
            <Layout>
              <Component {...pageProps} />
            </Layout>
          </AppLayout>
        </DynamicAuthProvider>
      </DynamicReduxProvider>
    </>
  )
}

// Export the App component directly since we're using useWrappedStore
export default AppWithLayout;
